import { css } from '@emotion/react'
import { Icon, PagedTable, TabItem, Tabs, Tooltip } from 'bold-ui'
import { IconLabel } from 'components/IconLabel'
import { usePagination } from 'components/table/usePagination'
import { VisualizarPaginaButton } from 'components/VisualizarPaginaButton'
import { useAcompanhamentoPreNatalExamesQuery } from 'graphql/hooks.generated'
import {
  AcompanhamentoPreNatalStatusExameEnum,
  ExamesPreNatalDto,
  TipoAcompanhamentoEnum,
  TrimestreGestacional,
} from 'graphql/types.generated'
import { Fragment, useState } from 'react'
import { formatDate } from 'util/date/formatDate'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { statusExamePreNatalRecord, trimestreGestacionalRecord } from '../model-acompanhamentoPreNatal'
import { DetalhamentoExameModal } from './DetalhamentoExameModal'

interface ExamesPreNatalCardProps {
  prontuarioId: ID
  dataInicioGestacao: Date
  loading?: boolean
}

const renderUltimoStatusData = (exame: ExamesPreNatalDto) => {
  const status = statusExamePreNatalRecord[exame.status]
  const showDate = exame.status === AcompanhamentoPreNatalStatusExameEnum.RESULTADO && exame.ultimaDataResultado
  const text = `${status.label}${showDate ? ` - ${formatDate(exame.ultimaDataResultado)}` : ''}`
  return <IconLabel icon={status.icon} text={text} color={status.color} hSpacing={0.5} />
}

export function ExamesPreNatalCard(props: Readonly<ExamesPreNatalCardProps>) {
  const { prontuarioId, dataInicioGestacao, loading } = props

  const [trimestreGestacional, setTrimestreGestacional] = useState(TrimestreGestacional.PRIMEIRO_TRIMESTRE)
  const [sort, setSort] = useState(['nomeProcedimento'])
  const [exameSelected, setExameSelected] = useState<ExamesPreNatalDto>()
  const [openModal, setOpenModal] = useState(false)

  const { data, loading: loadingExames } = useAcompanhamentoPreNatalExamesQuery({
    variables: {
      input: { prontuarioId, dataInicioGestacao, trimestreGestacional, sort: sort.first() },
    },
    skip: !prontuarioId || loading || !dataInicioGestacao,
  })

  const { paginatedItems, tableProps } = usePagination({
    items: data?.acompanhamentoPreNatalExames || [],
    initialPageSize: 8,
    sizeOptions: [8],
  })

  const renderVisualizarInformacoes = (exame: ExamesPreNatalDto) => {
    const disabled =
      exame.registrosNoTrimestre === 0 && exame.status !== AcompanhamentoPreNatalStatusExameEnum.RESULTADO_ANTERIOR

    const handleOnClick = () => {
      setExameSelected(exame)
      setOpenModal(true)
    }

    return (
      <VisualizarPaginaButton
        tooltipText={!disabled ? 'Visualizar informações' : ''}
        disabled={disabled}
        onClick={handleOnClick}
      />
    )
  }

  return (
    <Fragment>
      <AcompanhamentoCard
        id='exames-pre-natal'
        title='Exames'
        tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
        headerContent={
          <Tooltip text='Os dados são aqueles que foram incluídos na parte de “Solicitação de exames/procedimentos” e “Resultados de exames” do SOAP do atendimento'>
            <Icon icon='infoCircleFilled' size={1} />
          </Tooltip>
        }
      >
        <Tabs style={styles.noBottomLine}>
          <TabItem
            active={trimestreGestacional === TrimestreGestacional.PRIMEIRO_TRIMESTRE}
            onClick={() => setTrimestreGestacional(TrimestreGestacional.PRIMEIRO_TRIMESTRE)}
          >
            {trimestreGestacionalRecord[TrimestreGestacional.PRIMEIRO_TRIMESTRE]}
          </TabItem>
          <TabItem
            active={trimestreGestacional === TrimestreGestacional.SEGUNDO_TRIMESTRE}
            onClick={() => setTrimestreGestacional(TrimestreGestacional.SEGUNDO_TRIMESTRE)}
          >
            {trimestreGestacionalRecord[TrimestreGestacional.SEGUNDO_TRIMESTRE]}
          </TabItem>
          <TabItem
            active={trimestreGestacional === TrimestreGestacional.TERCEIRO_TRIMESTRE}
            onClick={() => setTrimestreGestacional(TrimestreGestacional.TERCEIRO_TRIMESTRE)}
          >
            {trimestreGestacionalRecord[TrimestreGestacional.TERCEIRO_TRIMESTRE]}
          </TabItem>
        </Tabs>
        <PagedTable
          rows={paginatedItems || []}
          size={tableProps.pageSize}
          loading={loading || loadingExames}
          sort={sort}
          onSortChange={setSort}
          columns={[
            {
              name: 'nomeProcedimento',
              header: 'Nome',
              sortable: true,
              render: (exame: ExamesPreNatalDto) =>
                `${exame.nomeProcedimento.capitalize()} - ${exame.codigoProcedimento}`,
            },
            {
              name: 'status',
              header: 'Último status e data',
              sortable: true,
              render: renderUltimoStatusData,
            },
            {
              name: 'registrosNoTrimestre',
              header: 'Registros no trimestre',
              render: (exame: ExamesPreNatalDto) => exame.registrosNoTrimestre,
            },
            {
              name: 'visualizarInformacoes',
              header: '',
              style: styles.lupa,
              render: renderVisualizarInformacoes,
            },
          ]}
          {...tableProps}
        />
      </AcompanhamentoCard>
      {openModal && (
        <DetalhamentoExameModal
          prontuarioId={prontuarioId}
          nomeProcedimento={exameSelected.nomeProcedimento}
          codigoProcedimento={exameSelected.codigoProcedimento}
          procedimentoId={exameSelected.procedimentoId}
          registrosNoTrimestre={exameSelected.registrosNoTrimestre}
          status={exameSelected.status}
          dataInicioCondicao={dataInicioGestacao}
          tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
          onClose={() => setOpenModal(false)}
        />
      )}
    </Fragment>
  )
}

const styles = {
  lupa: css`
    padding: 0;
  `,
  noBottomLine: css`
    border-bottom: 0;
  `,
}
